/**
 * Generically handles various error states around a field
 * @module elements/fields/entry-error
 */

import $ from 'jquery';
import get from 'lodash/get';
import stache from '../../lib/stache';
import registerJQueryPlugin, { noopActions } from '../../lib/register-jquery-plugin';

/**
 * Templates used as an absolute fallback
 * @typedef {Object} EntryErrorDefaultTemplates
 * @property {string} requiredTemplate - Template for required error message
 * @property {string} invalidTemplate - Template for invalid error message
 */
export const DEFAULT_TEMPLATES = {
  requiredTemplate: 'valid entry required',
  invalidTemplate: 'enter a valid entry',
};
/**
 * Configurable options entry error
 * @typedef {Object} EntryErrorOptions
 * @property {string} label - Label or name of the field
 * @property {string} requiredAttr - Attribute used for indicating field
 * is required
 * @property {string} entryErrorRequiredMessage - Overrides the default
 * required error message
 * @property {string} entryErrorInvalidMessage - Overrides the default
 * required invalid message
 * @property {string} requiredTemplate - Template used for the required error
 * message
 * @property {string} invalidTemplate - Template used for the invalid error
 * message
 * @property {string} selectRequiredTemplate - Template used for the required
 * error message for a select element
 */
/**
 * @type EntryErrorOptions
 */
const DEFAULT_ERROR_LABEL_OPTIONS = {
  label: null,
  requiredAttr: 'data-validate-not-empty',
  entryErrorRequiredMessage: '',
  entryErrorInvalidMessage: '',
  requiredTemplate: '{{label}} entry required',
  invalidTemplate: 'enter a valid {{label}}',
  selectRequiredTemplate: '{{label}} selection required',
};
const EVENT_NS = '.entry-label';
const ELEMENT_ERROR_TEMPLATES = {
  select: 'selectRequiredTemplate',
};
/**
 * Responsible for taking event names and appending a namespace
 * @param {Object} events - Key/value mapping of events
 * @returns {Object} Applicable events containing a namespace
 */
const appendNamespaceToEvents = events =>
  Object
    .keys(events)
    .reduce(
      (acc, name) => Object.assign({}, acc, { [name]: events[name] + EVENT_NS }),
      {},
    );
export const EVENTS = {
  INVALID: 'invalid',
  BLUR: 'blur',
  CHANGE: 'change',
  INPUT: 'input',
};

const { INVALID, BLUR, CHANGE, INPUT } = appendNamespaceToEvents(EVENTS);

/**
 * Performs any sort of clean up
 * @param {jQuery} $el - Form field
 * @param {Function} deregister - Internally removes the plugin from the
 * registery
 */
const destroy = ($el, deregister) => () => {
  $el.off(EVENT_NS);
  deregister();
};

/**
 * Responsible for resetting an error on a field
 * @param {HTMLElement} el - Form field
 */
const resetField = (el) => {
  el.setCustomValidity('');
  el.setAttribute('aria-invalid', false);
};

/**
 * Responsible for resetting an error on a field based on an event
 * @param {jQuery.Event} e - A dom event
 */
const resetFieldFromEvent = (e) => {
  resetField(e.target);
};

/**
 * Responsible creating the ability to set an error message
 * @param {jQuery} $el - Form field
 * @param {string} requiredMessage - Error message when a field is required
 * @param {string} invalidMessage - Error message when a field is invalid
 * @returns {Function} Called on to set a possible error message
 */
const makeSetMessage = ($el, requiredMessage, invalidMessage) => () => {
  const el = $el[0];
  if (!$el.val()) {
    $el.attr('aria-invalid', true);
    el.setCustomValidity(requiredMessage);
    return;
  }
  if ($el.data('entryErrorDisableInvalid')) {
    $el.attr('aria-invalid', false);
    return;
  }
  if ($el.is(':invalid')) {
    $el.attr('aria-invalid', true);
    el.setCustomValidity(invalidMessage);
    return;
  }

  
  resetField(el);
};

/**
 * Responsible for applying an error if applicable
 * @param {jQuery} $el - Form field
 * @param {Function} setMessage - If applicable sets an error message
 */
const makeApplyError = ($el, setMessage) => () => {
  const el = $el[0];
  const { validateOnlyOnSubmit } = get(el, 'form.dataset');
  if (validateOnlyOnSubmit) {
    return;
  }
  setMessage();
  el.checkValidity();
};
/**
 * Responsible obtaining an error message
 * @param {string} custom - A custom error message
 * @param {string} template - Template used for the error message
 * @param {Object} data - Data used for the error message template
 */
const getMessage = (custom, template, data) => custom || stache(template, data);
/**
 * Determines which template or message should be used from most specific to
 * most general based on a number of configured values.
 * @param {string|undefined} label - Name associated to field
 * @param {string|undefined} errorMessage - Message passed in on a field level
 * @param {string} errorType - Type of error
 * @returns {string} Template or message
 */
const getTemplate = (label, errorMessage, errorType) => {
  const template = DEFAULT_ERROR_LABEL_OPTIONS[errorType];
  const defaultMessage = DEFAULT_TEMPLATES[errorType];
  // This means the error message was provided on a field level, so that wins
  // over any other option.
  if (errorMessage) {
    return errorMessage;
  }
  // By default, a label is required since the default template is dependent
  // on the dynamic value of the label. There is a chance that the template
  // could still be undefined since the template can be undefined. In those
  // cases, just use the default template. This should at least indicate
  // something is broken without seeing template variables.
  if (label) {
    return template;
  }
  // If no label is provided, we cannot use the template, so fallback to the
  // default message as the lowest common denominator.
  return defaultMessage;
};
/**
 * Responsible for determining whether an element level required template is
 * needed. This comes as a request where based on an element, a different
 * required message can be used, which adds more complexity but does meet the
 * request.
 * @param {string} nodeName - Element name
 * @param {string} defaultRequiredTemplate - Default template when a field level
 * template cannot be found.
 * @param {EntryErrorOptions} options - Configurable options
 * @returns {string} Template to use when a field specific template is needed
 */
const getElementLevelTemplate = (nodeName, defaultRequiredTemplate, options) => {
  const elementLevelTemplate = ELEMENT_ERROR_TEMPLATES[nodeName];
  if (elementLevelTemplate) {
    return options[elementLevelTemplate] || defaultRequiredTemplate;
  }
  return defaultRequiredTemplate;
};
/**
 * Responsible for handling errors in a standardized way. In particular, it'll
 * create consistent error handling based on the type of errors.
 * @param {HTMLElement} el - Element with error in question
 * @param {EntryErrorOptions} options - Configurable options
 * @param {Object} actions - Actions to be called on
 * @return {Object} Methods attached to an entry error
 */
export default function entryError(el, options = {}, { deregister } = noopActions) {
  const $el = $(el);
  const opts = Object.assign({}, DEFAULT_ERROR_LABEL_OPTIONS, options, el.dataset);
  const {
    label,
    requiredAttr,
    requiredTemplate: requiredTemplateOption,
    invalidTemplate: invalidTemplateOption,
    entryErrorRequiredMessage: customRequiredMessage,
    entryErrorInvalidMessage: customInvalidMessage,
  } = opts;
  const nodeName = $el.prop('nodeName').toLowerCase();
  const requiredFieldTemplate = getElementLevelTemplate(nodeName, requiredTemplateOption, opts);
  const requiredTemplate = getTemplate(label, requiredFieldTemplate, 'requiredTemplate');
  const invalidTemplate = getTemplate(label, invalidTemplateOption, 'invalidTemplate');
  const requiredMessage = getMessage(customRequiredMessage, requiredTemplate, { label });
  const invalidMessage = getMessage(customInvalidMessage, invalidTemplate, { label });
  const setMessage = makeSetMessage($el, requiredMessage, invalidMessage);
  const applyError = makeApplyError($el, setMessage);
  $el
    .attr(requiredAttr, true)
    .attr('aria-required', true)
    .on(BLUR, applyError)
    .on(INVALID, setMessage)
    .on(INPUT, resetFieldFromEvent);

  if (nodeName === 'select') {
    $el.on(CHANGE, applyError);
  }
  return {
    destroy: destroy($el, deregister),
  };
}
registerJQueryPlugin('entryError', entryError);


// WEBPACK FOOTER //
// ./styleguide/src/assets/elements/fields/entry-error.js